import { http, setApiUrl } from "@/http";
import storage from "@/storage";

export default {
  namespaced: true,

  state: {
    user: {},
    environment: {},
    isSSOLogin: false, // Flag para identificar se o login foi via SSO
  },

  getters: {
    getFirstName(state) {
      const name = state.isSSOLogin ? state.user?.userId : state.user?.name;
      return name ? name.split(' ')[0] : '';
    },
    getFullName(state) {
      return state.user?.name || state.user?.userName || '';
    },
    getUserName(state) {
      return state.user?.id || state.user?.userId || state.user?.userName || '';
    },
    getUserRoles(state) {
      return state.user?.tipo || state.user?.type || '';
    },
    getUser(state) {
      return state.user || {};
    },
    getEnvironment(state) {
      return state.environment || '';
    },
    isSSOLogin(state) {
      return state.isSSOLogin || false;
    }
  },

  mutations: {
    SET_USER(state, payload) {

      state.user = payload;

      // Persistir usuário no localStorage
      if (payload && typeof payload === 'object' && Object.keys(payload).length > 0) {
        localStorage.setItem('universe-user', JSON.stringify(payload));
      }
    },
    SET_ENVIRONMENT(state, environment) {
      state.environment = environment;
      // Persistir environment no localStorage
      if (environment) {
        storage.setUniverseEnvironment(environment);
      }
    },
    SET_SSO_LOGIN(state, isSSO) {
      state.isSSOLogin = isSSO;
      // Persistir SSO flag no localStorage
      if (isSSO) {
        localStorage.setItem('universe-sso-flag', 'true');
      } else {
        localStorage.removeItem('universe-sso-flag');
      }
    },
  },

  actions: {
    
    // Buscar dados do usuário via API no login SSO
    async fetchUserData({ dispatch, commit }) {

      const ssoEnv = sessionStorage.getItem('universe-env-sso');
      if (ssoEnv) {
        dispatch('setApiUrlByEnvironment', ssoEnv);
      }

      return http.get('/api/Navegacao/Authentication/me', {
        withCredentials: true,
      })
        .then((response) => {
          if (response.status === 200) {
            const userData = response.data;

            // LIMPAR FALHA DE SSO
            localStorage.removeItem('universe-sso-auth-failed');

            // Armazenar dados no localStorage
            localStorage.setItem('universe-user', JSON.stringify(userData));

            // Marcar como SSO login
            localStorage.setItem('universe-sso-flag', 'true');

            return Promise.resolve(userData);
          } else {
            return Promise.reject(response);
          }
        })
        .catch((error) => {

          // Marcar que houve falha na autenticação SSO para evitar loop de redirecionamento
          localStorage.setItem('universe-sso-auth-failed', 'true');

          if (error.status === 401 || error.response?.status === 401) {

            // Exibir alerta e retornar null (não rejeitar a Promise)
            new Promise((resolve) => {
              commit('ui/ALERT', {
                text: 'erro.autenticacao.sso',
                visible: true,
                resolve: resolve
              }, { root: true });
            }).then(() => {
              // console.log('%c[STORE AUTH] Alerta confirmado');
            });

            return null;
          }

          return null;
        });
    },

    // Restaura o estado de autenticação ao recarregar a aplicação
    async restoreAuthState({ commit, dispatch, state }) {

      // Verificar se é login SSO e buscar dados via API se necessário
      const isSSOLogin = localStorage.getItem('universe-sso-flag') === 'true';

      if (isSSOLogin && !localStorage.getItem('universe-user')) {
        try {
          // Tentar buscar dados do usuário via API
          await dispatch('fetchUserData');
        } catch (error) {
          localStorage.removeItem('universe-sso-flag');
          localStorage.removeItem('universe-sso-auth-failed');
          commit('SET_SSO_LOGIN', false);
          // console.warn('[AUTH] Falha ao restaurar dados SSO, continuando com login tradicional');
        }
      }

      // Restaurar dados do usuário persistidos
      const persistedUser = localStorage.getItem('universe-user');
      if (persistedUser) {
        try {
          const userData = JSON.parse(persistedUser);
          commit('SET_USER', userData);
          
          // Se não for login SSO, verificar se existe tempo de expiração de sessão
          if (!isSSOLogin) {
            const expirationTimestamp = localStorage.getItem('universe-session-expiration');
            if (expirationTimestamp) {
              const timeRemaining = parseInt(expirationTimestamp) - Date.now();
              
              if (timeRemaining > 0) {
                // Configurar timer restante
                window.authExpirationTimer = setTimeout(() => {
                  // exibir alerta do Vuex  e esperar confirmação para realizar logout
                  new Promise((resolve) => {
                    commit('ui/ALERT', {
                      text: 'sessao.expirada',
                      resolve: resolve,
                      visible: true
                    }, { root: true });
                  }).then(() => {
                    dispatch("handleSessionExpired");
                  });
                }, timeRemaining);
              } else {
                dispatch("handleSessionExpired");
              }
            }
          }
        } catch (error) {
          localStorage.removeItem('universe-user');
          localStorage.removeItem('universe-session-expiration');
        }
      }

      // Processo tradicional de restauração
      // Verificar token diretamente no sessionStorage
      const directToken = sessionStorage.getItem('universe-sid');

      if (directToken) {
        storage.setUniverseToken(directToken);
      }

      // Verificar se é SSO e restaurar environment
      const ssoEnv = sessionStorage.getItem('universe-env-sso');
      if (ssoEnv) {
        commit('SET_ENVIRONMENT', ssoEnv);
        dispatch('setApiUrlByEnvironment', ssoEnv);
        // Limpar após usar
        sessionStorage.removeItem('universe-env-sso');
      } else {
        // Se não houver SSO, restaurar environment do storage normal (login tradicional)
        const savedEnvironment = storage.getUniverseEnvironment();
        if (savedEnvironment) {
          commit('SET_ENVIRONMENT', savedEnvironment);
          dispatch('setApiUrlByEnvironment', savedEnvironment);
        }
      }

      // Restaurar SSO flag se existir
      const ssoFlag = localStorage.getItem('universe-sso-flag');
      if (ssoFlag === 'true') {
        commit('SET_SSO_LOGIN', true);
      }
    },

    setApiUrlByEnvironment({ commit }, environment) {
      setApiUrl(environment);
    },
    
    async actionLogin({ commit, dispatch, state }, payload) {
      const result = await http
        .post("/api/Navegacao/Authentication/login", payload)
        .then((response) => {
          if (response.status === 200 && response.data.token !== "") {
            commit("SET_USER", response.data.user);

            storage.setUniverseToken(response.data.token);

            // Configurar timer para logout automático quando a sessão(expiresInMinutes) expirar apenas se não for login via SSO
            if (!state.isSSOLogin) {
              // Converter minutos para milissegundos
              const expirationTime = response.data.expiresInMinutes * 60 * 1000;

              // Salvar timestamp absoluto de expiração no localStorage
              const expirationTimestamp = Date.now() + expirationTime;
              localStorage.setItem('universe-session-expiration', expirationTimestamp.toString());

              // Configurar timer para logout automático quando a sessão(expiresInMinutes) expirar
              window.authExpirationTimer = setTimeout(() => {
                // exibir alerta do Vuex  e esperar confirmação para realizar logout
                new Promise((resolve) => {
                  commit('ui/ALERT', {
                    text: 'sessao.expirada',
                    resolve: resolve,
                    visible: true
                  }, { root: true });
                }).then(() => {
                  // Realizar logout e redirecionar
                  dispatch("handleSessionExpired");
                });
              }, expirationTime);
            }

            return Promise.resolve(response);
          } else {
            return Promise.reject(response);
          }
        })
        .catch((error) => {
          const err = {
            status: error.status,
            code: error.code || error.status,
            tMessage: "",
            message: ""
          };
          if (error.status === 401) {
            err.tMessage = "login.auth.failed.unauthorized";
          }
          else if (error.status === 403) {
            err.tMessage = error.data.errorCode;
          }
          else if (error.status === 500) {
            err.tMessage = "login.auth.failed.internal.server.error";
          } else if (error.code === "ERR_NETWORK") {
            err.tMessage = "login.auth.failed.err.network";
          } else {
            err.message = `${error.status || ''} ${error.code || ''} ${error.message || ''}`
          }

          return Promise.reject(err);
        });
      return result;
    },

    // Action para lidar com a expiração da sessão
    handleSessionExpired({ dispatch }) {
      dispatch("actionLogout");
      window.location.href = '/';
    },

    actionLogout({ commit }) {

      if (window.authExpirationTimer) {
        clearTimeout(window.authExpirationTimer);
        window.authExpirationTimer = null;
      }

      storage.removeUniverseToken();
      storage.removeUniverseEnvironment();

      localStorage.removeItem('universe-user');
      localStorage.removeItem('universe-session-expiration'); // Limpar tempo de expiração
      localStorage.removeItem('universe-sso-flag'); // Limpar flag SSO
      localStorage.removeItem('universe-sso-auth-failed'); // Limpar flag de falha SSO
      sessionStorage.removeItem('universe-env-sso');

      commit("SET_USER", {});
      commit("SET_ENVIRONMENT", {});
      commit("SET_SSO_LOGIN", false);

    },
  },
};